// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import { FormField, FormState } from "@werner/XDL/Utilities/form";

const formId = "requestRMAZipCode";

export interface ContactFormState extends FormState {
    fields: {
        name: FormField<string>;
        phone: FormField<string>;
        address1: FormField<string>;
        address2: FormField<string>;
        city: FormField<string>;
        state: FormField<string>;
        zipCode: FormField<string>;
        pickupDate: FormField<string>;
        returnNotes: FormField<string>;
    };
}

export const initialContactFormState: ContactFormState = {
    isValid: false,
    isSubmitted: false,
    fields: {
        name: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "contactForm_contactName",
                name: "contactName",
                label: translate("Contact Info"),
                placeholder: translate("Contact Name"),
                required: true,
                maxLength: 50,
            },
            validatorsOptions: () => ({
                customReuiredErrorMessage: translate("Contact Info is required"),
            }),
        },
        phone: {
            value: "",
            validators: ["required", "phone"],
            attrs: {
                id: "contactForm_phoneNumber",
                name: "phoneNumber",
                label: translate("Phone Number"),
                placeholder: "( ___ ) ___ - ____",
                required: true,
                maxLength: 12,
            },
            validatorsOptions: () => ({
                customReuiredErrorMessage: translate("Phone Number is required"),
            }),
        },
        address1: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "contactForm_address1",
                name: "address1",
                label: translate("Address1"),
                placeholder: translate("Your First Address"),
                required: true,
                maxLength: 50,
            },
            validatorsOptions: () => ({
                customReuiredErrorMessage: translate("Address1 is required"),
            }),
        },
        address2: {
            value: "",
            validators: [],
            attrs: {
                id: "contactForm_address2",
                name: "address2",
                label: translate("Address2 (Optional)"),
                placeholder: translate("Your Second Address"),
                maxLength: 50,
            },
        },
        state: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "contactForm_state",
                name: "state",
                label: translate("State"),
                required: true,
                maxLength: 2,
            },
            validatorsOptions: () => ({
                customReuiredErrorMessage: translate("State is required"),
            }),
        },
        city: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "contactForm_city",
                name: "city",
                label: translate("City"),
                placeholder: translate("Your City"),
                required: true,
            },
            validatorsOptions: () => ({
                customReuiredErrorMessage: translate("City is required"),
            }),
        },
        zipCode: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "contactForm_zip",
                name: "zip",
                label: translate("Zip"),
                placeholder: translate("Zip Code"),
                required: true,
                maxLength: 10,
                type: "postalCode",
                ...{ formId },
            },
            validatorsOptions: () => ({
                customReuiredErrorMessage: translate("Zip is required"),
            }),
        },
        pickupDate: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "contactForm_pickupDate",
                name: "pickupDate",
                label: translate("Pickup Date"),
                placeholder: translate("mm / dd / yyyy"),
                required: true,
            },
            validatorsOptions: () => ({
                customReuiredErrorMessage: translate("Pickup Date is required"),
            }),
        },
        returnNotes: {
            value: "",
            validators: ["required"],
            validatorsOptions: () => ({
                customReuiredErrorMessage: translate("Return Notes is required"),
            }),
        },
    },
};
